import { FetchBoundary } from "providers/FetchBoundary";
import { Link, useNavigate } from "react-router-dom";
import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import { ArrowLeft, Clock } from "untitledui-js-base";
import { ApplicantRadioCard } from "./ApplicantRadioCard";
import { FC, useState } from "react";
import { UseQueryResult } from "@tanstack/react-query";
import { Application, MakeDecisionRequirements } from "types/application";
import {
  ApplicantStatus,
  ApplicantType,
  ApplicationStatus
} from "shared/types";
import { ActivityLog } from "shared/app/ActivityLog";
import { MakeDecisionModal } from "shared/app/MakeDecisionModal";
import { useAuthContext } from "providers/Auth";
import { getFullName } from "shared/utils/get-full-name";
import { ContextMenuPopover } from "shared/components/ContextMenuPopover";
import { DecisionButton } from "shared/app/DecisionButton";
import { useDecisionButton } from "shared/app/DecisionButton/useDecisionButton";
import { InviteAndCreateModal } from "shared/app/InviteAndCreateModal";
import { HeaderDropdownMenu } from "./HeaderDropdownMenu";
import { Note02Icon } from "@hugeicons-pro/core-stroke-rounded";
import { useNotesController } from "shared/queries/useNotesController";
import { HugeiconsIcon } from "@hugeicons/react";
import { Notes } from "shared/app/Notes";

export const LayoutHeader: FC<{
  setSearchParams: (newState: Record<string, string>) => void;
  searchParams: Record<string, string>;
  applicationMeta: UseQueryResult<Application | undefined, unknown>;
  makeDecisionRequirements?: MakeDecisionRequirements[];
  templateId: string;
}> = ({
  applicationMeta,
  makeDecisionRequirements,
  searchParams,
  setSearchParams,
  templateId
}) => {
  const navigate = useNavigate();
  const { type } = useAuthContext();
  const applicationStatus = applicationMeta.data?.status;
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showMakeDecisionModal, setShowMakeDecisionModal] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const notesController = useNotesController(String(applicationMeta.data?.id));

  const { appActions, openUndoModal, UndoDecisionModal } = useDecisionButton({
    applicationId: String(applicationMeta.data?.id ?? ""),
    status: applicationStatus || ApplicationStatus.Started,
    applicants: applicationMeta.data?.applicants || [],
    application: applicationMeta.data,
    decisionAt: applicationMeta.data?.decision_at ?? null,
    requirements: makeDecisionRequirements
  });

  const selectedApplicant = applicationMeta.data?.applicants.find(
    (app) => String(app.id) === String(searchParams.applicantId)
  );
  const canViewLead = type.isBoomCRM && Boolean(selectedApplicant?.lead_id);

  const mobileMenuActions = [
    {
      title: "Invite applicant",
      onClick: () => setShowInviteModal(true),
      disabled: appActions.inviteApplicant.disabled,
      tooltip: appActions.inviteApplicant.tooltip
    },
    ...(!appActions.makeDecision.hidden
      ? [
          {
            title: "Make Decision",
            onClick: () => setShowMakeDecisionModal(true),
            disabled: appActions.makeDecision.disabled,
            tooltip: appActions.makeDecision.tooltip
          }
        ]
      : []),
    ...(!appActions.undoDecision.hidden
      ? [
          {
            title: "Undo decision",
            onClick: () => openUndoModal(),
            disabled: appActions.undoDecision.disabled,
            tooltip: appActions.undoDecision.tooltip
          }
        ]
      : []),
    {
      title: "Activity log",
      onClick: () => setShowActivityLog(true)
    },
    ...(canViewLead
      ? [
          {
            title: "View lead",
            onClick: () => navigate(`/leads/${selectedApplicant?.lead_id}`)
          }
        ]
      : [])
  ];

  return (
    <div className="no-print bg-white">
      <div className="flex justify-between border-b border-alpha-40 p-3 md:px-6 md:flex-nowrap flex-wrap gap-2 h-16">
        <div className="flex items-center gap-2 md:gap-6 w-full ">
          <Button
            size={ButtonSize.SM}
            Icon={ArrowLeft}
            onClick={() => {
              // Navigate back to the previous page if available
              // If no history, navigate to the applications page
              if (window.history.length > 2) return navigate(-1);
              return navigate(`/applications/${applicationMeta.data?.id}`);
            }}
          >
            Back
          </Button>

          <div className="ml-auto md:hidden">
            <ContextMenuPopover actions={mobileMenuActions} />
          </div>
        </div>

        <div className="gap-2 hidden md:flex items-center">
          <Button
            borderLess
            className="!text-content-400 !stroke-content-400"
            size={ButtonSize.SM}
            variant={ButtonVariant.BASE}
            Icon={Clock}
            onClick={() => setShowActivityLog(true)}
          >
            Activity log
          </Button>
          <Link to={`/applications/${applicationMeta.data?.id}`}>
            <Button size={ButtonSize.SM}>Group application</Button>
          </Link>
          <Button
            variant={ButtonVariant.BASE}
            size={ButtonSize.SM}
            onClick={() => setShowNotesModal?.(true)}
          >
            <div className="flex items-center gap-2">
              <Show if={Boolean(notesController.totalNotesCount)}>
                <span className="relative inline-flex shrink-0">
                  <HugeiconsIcon
                    icon={Note02Icon}
                    size={20}
                    strokeWidth={1.5}
                  />
                  <span className="absolute -top-1 -right-1.5 flex h-[17.5px] min-w-[17.5px] items-center justify-center rounded-sm bg-primary-500 text-[10px] font-medium leading-4 text-white border-[1.5px] border-white">
                    {notesController.totalNotesCount}
                  </span>
                </span>
              </Show>
              <span>Notes</span>
            </div>
          </Button>
          <DecisionButton
            appActions={appActions}
            onUndoDecision={openUndoModal}
            onMakeDecision={() => setShowMakeDecisionModal(true)}
            makeDecisionLabel="Make Decision"
          />
          <HeaderDropdownMenu
            inviteApplicantAction={appActions.inviteApplicant}
            canViewLead={canViewLead}
            leadId={selectedApplicant?.lead_id}
            templateId={templateId}
            setShowInviteApplicantsModal={setShowInviteModal}
          />
        </div>

        {applicationMeta.data && showMakeDecisionModal && (
          <MakeDecisionModal
            opened={showMakeDecisionModal}
            onClose={() => {
              applicationMeta.refetch();
              setShowMakeDecisionModal(false);
            }}
            applicationId={String(applicationMeta.data.id)}
          />
        )}

        <UndoDecisionModal />

        {showInviteModal && (
          <InviteAndCreateModal
            disabledRoles={
              [
                ...(applicationMeta.data?.applicants || []),
                ...(applicationMeta.data?.pending_magic_links || [])
              ].some((applicant) =>
                [ApplicantType.Applicant, ApplicantType.Occupant].includes(
                  applicant.role as ApplicantType
                )
              )
                ? []
                : [ApplicantType.CoSigner]
            }
            applicationId={Number(applicationMeta.data?.id)}
            open={showInviteModal}
            setOpen={() => setShowInviteModal(false)}
            propertyId={applicationMeta.data?.property.id ?? 0}
            unitId={applicationMeta.data?.unit.id ?? 0}
            propertyName={applicationMeta.data?.property.name}
            unitName={applicationMeta.data?.unit.title}
            onInviteRefreshKey={[
              "APPLICATION",
              String(applicationMeta.data?.id)
            ]}
          />
        )}
      </div>

      <div className="grid w-full grid-flow-row auto-cols-fr grid-cols-[repeat(auto-fit,minmax(200px,1fr))]">
        <FetchBoundary
          loading={applicationMeta.isLoading || !applicationMeta.data}
          error={applicationMeta.error}
        >
          {applicationMeta.data
            ? applicationMeta.data.applicants.map((app) => {
                const checked =
                  String(app.id) === String(searchParams.applicantId);
                return (
                  <ApplicantRadioCard
                    applicant={app}
                    key={app.id}
                    onClick={() => {
                      document
                        .getElementById("report_section")
                        ?.scrollTo({ top: 0, behavior: "smooth" });

                      // Keep the current report in the URL and only switch the
                      // applicant. The report page decides whether this applicant
                      // has that report and falls back to the rental application
                      // if not (see ApplicationsIdReportView).
                      if (!checked)
                        setSearchParams({
                          ...searchParams,
                          applicantId: String(app.id)
                        });
                    }}
                    fullName={getFullName({
                      firstName: app.first_name,
                      lastName: app.last_name,
                      middleName: app.middle_name,
                      short: true
                    })}
                    role={app.role}
                    checked={checked}
                    isSubmitted={
                      app.application_status === ApplicantStatus.Submitted
                    }
                    id={String(app.id)}
                    isPrimary={app.is_primary}
                  />
                );
              })
            : null}
        </FetchBoundary>
      </div>

      {/* Show removes unnecessary fetch request */}
      <Show if={showActivityLog}>
        <ActivityLog
          showActivityLog={showActivityLog}
          setShowActivityLog={setShowActivityLog}
          applicationId={String(applicationMeta.data?.id) || ""}
        />
      </Show>

      <Show if={showNotesModal}>
        <Notes
          showNotes={showNotesModal}
          setShowNotes={setShowNotesModal}
          application={applicationMeta.data as Application}
          notesController={notesController}
        />
      </Show>
    </div>
  );
};
